// Angular
import { Injectable } from '@angular/core';

// RxJS
import { BehaviorSubject, filter, map, Observable, switchMap } from 'rxjs';

// Third-party
import lodash from 'lodash';

@Injectable({ providedIn: 'root' })
export abstract class ComparableDataService<T> {
  /**
   * The data, which can be either an array of T or a single T object.
   * - Nullable to differentiate between empty/uninitialized data.
   */
  private readonly data$$ = new BehaviorSubject<T | T[] | null>(null);

  /**
   * The original data, which can be either an array of T or a single T object.
   * - Nullable to differentiate between empty/uninitialized data.
   */
  private readonly originalData$$ = new BehaviorSubject<T | T[] | null>(null);

  /**
   * The data exposed as an Observable.
   * - Will not emit a value until the data$$ stream emits a non-null value.
   */
  get data$(): Observable<T | T[]> {
    return this.data$$.asObservable().pipe(
      filter((data: T | T[] | null) => data !== null),
      map((data: T | T[]) => data)
    );
  }

  /**
   * The data.
   */
  get data(): T | T[] | null {
    return this.data$$.value;
  }

  /**
   * The original data exposed as an Observable.
   * - Will not emit a value until the originalData$$ stream emits a non-null value.
   */
  get originalData$(): Observable<T | T[]> {
    return this.originalData$$.asObservable().pipe(
      filter((data: T | T[] | null) => data !== null),
      map((data: T | T[]) => data)
    );
  }

  /**
   * The original data.
   */
  get originalData(): T | T[] | null {
    return this.originalData$$.value;
  }

  /**
   * A boolean value indicating whether the data has changed.
   */
  get hasChanges$(): Observable<boolean> {
    return this.data$.pipe(
      switchMap((data: T | T[]) => {
        return this.originalData$.pipe(
          map((originalData: T | T[]) => {
            return this.checkHasChanged(data, originalData);
          })
        );
      })
    );
  }

  /**
   * A boolean value indicating whether the data has changed.
   */
  get hasChanges(): boolean {
    return this.checkHasChanged(this.data, this.originalData);
  }

  /**
   * A public method to set the data.
   *
   * @param data The data (T or T[]).
   */
  setData(data: T | T[]): void {
    this.data$$.next(data);
  }

  /**
   * A public method to set the original data.
   *
   * @param originalData The original data (T or T[]).
   */
  setOriginalData(originalData: T | T[] | null): void {
    this.originalData$$.next(originalData);
  }

  /**
   * A public method to revert the data to the original data.
   */
  revertData(): void {
    this.data$$.next(this.originalData);
  }

  /**
   * A private helper function to determine if two pieces of data are different.
   *
   * @param data1 The first piece of data.
   * @param data2 The second piece of data.
   *
   * @returns A boolean value indicating whether the two pieces of data are different.
   */
  private checkHasChanged(data1: T | T[] | null, data2: T | T[] | null): boolean {
    return !lodash.isEqual(data1, data2);
  }
}